import type { Moment } from "moment";
import {
  API_DATETIME_EMPTY,
  MOMENT_FMT_DD_SLASH_MMM_YYYY_HH_MM_A,
  MOMENT_FMT_EN_IN_DATE_ONLY,
  parseApiDateTimeMoment,
} from "./apiDateTime";
import { parseLoginActivityMoment } from "./loginActivityTime";

export type FormatLocalDateTimeOptions = {
  format?: string;
  locale?: string;
  emptyValue?: string;
  invalidValue?: string;
  assumeUtc?: boolean;
};

function normalizeLocale(locale: string): string {
  return locale.trim().replace(/_/g, "-").toLowerCase();
}

function isEmptyInput(input: string | number | null | undefined): boolean {
  if (input === null || input === undefined) return true;
  return typeof input === "string" && !input.trim();
}

/**
 * Parse API date/time strings into a moment in the browser's local timezone.
 * Handles legacy values like `29/06/2026 19:32` (DD/MM/YYYY hh:mm A) as UTC, then converts to local.
 */
export function parseLocalDateTime(
  input: string | number | null | undefined,
  options?: Pick<FormatLocalDateTimeOptions, "assumeUtc">
): Moment | null {
  if (isEmptyInput(input)) return null;

  if (options?.assumeUtc) {
    return parseApiDateTimeMoment(input, { assumeUtc: true });
  }

  return parseLoginActivityMoment(input) ?? parseApiDateTimeMoment(input);
}

export function formatLocalDateTime(
  input: string | number | null | undefined,
  options?: FormatLocalDateTimeOptions
): string {
  const {
    format = MOMENT_FMT_DD_SLASH_MMM_YYYY_HH_MM_A,
    locale = "en-in",
    emptyValue = API_DATETIME_EMPTY,
    invalidValue,
    assumeUtc = false,
  } = options ?? {};

  if (isEmptyInput(input)) return emptyValue;

  const parsed = parseLocalDateTime(input, { assumeUtc });
  if (!parsed?.isValid()) {
    if (invalidValue !== undefined) return invalidValue;
    return typeof input === "string" && input.trim() ? input.trim() : emptyValue;
  }

  // Always render in the browser local zone (API timestamps are typically UTC).
  return parsed.clone().local().locale(normalizeLocale(locale)).format(format);
}

export function formatLocalDateOnly(
  input: string | number | null | undefined,
  options?: Omit<FormatLocalDateTimeOptions, "format">
): string {
  return formatLocalDateTime(input, {
    ...options,
    format: MOMENT_FMT_EN_IN_DATE_ONLY,
  });
}
